import {
  Card,
  CardContent,
  CardHeader,
  CardSubTitle,
  CardTitle,
} from '@growdash/design-system/atoms';
import { Coins, Loader2, Star, Truck } from 'lucide-react';
import React from 'react';
import { CuisineOperationBenchmark } from '../../../generated';
import useCurrency from '../../../hooks/useCurrency';
import useTabState from '../../../hooks/useTabState';
import BenchmarkCard from '../components/benchmark-card';

type BenchmarkType = 'delivery_time' | 'menu_price' | 'rating';

type BenchmarkEntries = {
  [key in BenchmarkType]: {
    title: string;
    icon: React.ReactNode;
    best: number;
    worst: number;
    unit?: string;
  };
};

type Props = {
  data: CuisineOperationBenchmark | undefined;
  isLoading?: boolean;
  className?: string;
  activeFilters: {
    brandName: string;
    cuisine: string;
  };
  avgPrice: number;
};
export default function CuisineBenchmark({
  data,
  isLoading,
  className = '',
  activeFilters,
  avgPrice,
}: Props) {
  const currency = useCurrency();
  const benchmarkEntries: BenchmarkEntries = {
    delivery_time: {
      title: 'Delivery Time',
      icon: <Truck size={18} className="text-tertiary" />,
      best: 5,
      worst: 60,
      unit: 'min',
    },

    menu_price: {
      title: 'Menu Price',
      icon: <Coins size={18} className="text-tertiary" />,
      best: 0,
      worst: 100,
      unit: currency,
    },
    rating: {
      title: 'Rating',
      icon: <Star size={18} className="text-tertiary" />,
      best: 5,
      worst: 0,
      unit: 'stars',
    },
  };
  const { filters } = useTabState('trends');

  const {
    delivery_time_comparison,
    delivery_time_current,
    max_delivery_time_comparison,
    max_menu_price_comparison,
    max_rating_comparison,
    menu_price_current,
    min_delivery_time_comparison,
    min_menu_price_comparison,
    min_rating_comparison,
    rating_comparison,
    rating_current,
  } = data ?? ({} as CuisineOperationBenchmark);

  return (
    <Card className={`${className}`}>
      <CardHeader>
        <CardTitle className="inline">Cuisine Operation Benchmarks</CardTitle>
        <CardSubTitle>
          {' '}
          <CardSubTitle>
            <span className="text-primary text-xs font-medium">
              {activeFilters.brandName}
            </span>
            <span className="mx-0.5 text-xs font-medium">vs</span>
            <span className="text-primary text-xs font-medium">
              {activeFilters.cuisine} cuisine
            </span>
          </CardSubTitle>
        </CardSubTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-12 max-sm:grid-cols-4 px-0 pt-0">
        {isLoading && (
          <div className="col-span-12 py-12 flex justify-center items-center animate-fade-in">
            <Loader2 className="animate-spin ease-in-out" />
          </div>
        )}
        {data && !isLoading && (
          <>
            <BenchmarkCard
              key={'benchmark-delivery-time'}
              title={benchmarkEntries.delivery_time.title}
              icon={benchmarkEntries.delivery_time.icon}
              className="animate-fade-in col-span-4 shadow-none rounded-none border-transparent p-4 max-sm:border-b-[1px] max-sm:border-b-border"
              cuisineAverage={delivery_time_comparison}
              value={delivery_time_current}
              best={min_delivery_time_comparison ?? 5}
              worst={max_delivery_time_comparison ?? 60}
              unit="min"
            />

            <BenchmarkCard
              key={'benchmark-menu-price'}
              title={benchmarkEntries.menu_price.title}
              icon={benchmarkEntries.menu_price.icon}
              className="animate-fade-in col-span-4 shadow-none rounded-none border-transparent p-4
                border-l-[1px] sm:border-l-border max-sm:border-b-[1px] max-sm:border-b-border"
              cuisineAverage={avgPrice}
              value={menu_price_current}
              best={min_menu_price_comparison ?? 5}
              worst={max_menu_price_comparison ?? 150}
            />
            <BenchmarkCard
              key={'benchmark-rating'}
              title={benchmarkEntries.rating.title}
              icon={benchmarkEntries.rating.icon}
              className="animate-fade-in col-span-4 shadow-none rounded-none border-transparent p-4
                border-l-[1px] sm:border-l-border"
              cuisineAverage={rating_comparison}
              value={rating_current}
              best={max_rating_comparison ?? 5}
              worst={min_rating_comparison ?? 60}
            />
          </>
        )}

        {!isLoading && !filters.cuisine && (
          <div className="py-12 flex justify-center items-center w-full col-span-12">
            <p className="text-muted-foreground text-center">
              Select a cuisine to see the benchmark
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
